/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { SystemScope } from '@opengeoweb/shared';
import type { SentryState, SentryStateMap } from '@opengeoweb/store';
import { pickEntities, pickList, pickRecord } from '@opengeoweb/store';
import type { WorkspaceModuleStore } from './types';
import type { WorkspaceListError } from './workspaceList/types';
import { WorkspaceListErrorType } from './workspaceList/types';

const isSystemScope = (scope?: SystemScope): boolean =>
  scope === SystemScope.system;

const pickWorkspaceListError = (
  error: WorkspaceListError,
): SentryState<WorkspaceListError> => {
  if (!error) {
    return undefined;
  }
  return error.type === WorkspaceListErrorType.TRANSLATABLE_ERROR
    ? { type: error.type, category: error.category, key: error.key }
    : { type: error.type, category: error.category };
};

export const workspaceSentryStateMap: SentryStateMap<
  Pick<WorkspaceModuleStore, 'workspace' | 'viewPresets' | 'workspaceList'>
> = {
  workspace: (workspace) => ({
    id: isSystemScope(workspace.scope) ? workspace.id : undefined,
    scope: workspace.scope,
    viewType: workspace.viewType,
    isLoading: workspace.isLoading,
    hasChanges: workspace.hasChanges,
    error: workspace.error && {
      type: workspace.error.type,
      message: workspace.error.message,
    },
    isTimeScrollingEnabled: workspace.isTimeScrollingEnabled,
    syncGroups: pickList(workspace.syncGroups, (syncGroup) => ({
      id: syncGroup.id,
      type: syncGroup.type,
      sourcePanelId: syncGroup.sourcePanelId,
    })),
    views: pickEntities(workspace.views, (view) => ({
      id: isSystemScope(view.scope) ? view.id : undefined,
      mosaicNodeId: view.mosaicNodeId,
      componentType: view.componentType,
      scope: view.scope,
      activeLayerId: view.activeLayerId,
    })),
  }),
  viewPresets: (viewPresets) => ({
    ids: viewPresets.ids,
    entities: pickRecord(viewPresets.entities, (entity) => ({
      panelId: entity.panelId,
      hasChanges: entity.hasChanges,
      isFetching: entity.isFetching,
      isViewPresetListDialogOpen: entity.isViewPresetListDialogOpen,
      error: entity.error && {
        component: entity.error.component,
        errorType: entity.error.errorType,
        message: entity.error.message,
      },
      filters: pickList(entity.filters, (filter) => ({
        id: filter.id,
        isSelected: filter.isSelected,
        isDisabled: filter.isDisabled,
      })),
    })),
    viewPresetDialog: viewPresets.viewPresetDialog && {
      action: viewPresets.viewPresetDialog.action,
      panelId: viewPresets.viewPresetDialog.panelId,
      shouldSetActiveViewPreset:
        viewPresets.viewPresetDialog.shouldSetActiveViewPreset,
    },
  }),
  workspaceList: (workspaceList) => ({
    isWorkspaceListDialogOpen: workspaceList.isWorkspaceListDialogOpen,
    error: pickWorkspaceListError(workspaceList.error),
    workspaceListFilters: pickList(
      workspaceList.workspaceListFilters,
      (filter) => ({
        id: filter.id,
        isSelected: filter.isSelected,
        isDisabled: filter.isDisabled,
      }),
    ),
    workspaceActionDialog: workspaceList.workspaceActionDialog && {
      action: workspaceList.workspaceActionDialog.action,
      hasSaved: workspaceList.workspaceActionDialog.hasSaved,
      error: pickWorkspaceListError(workspaceList.workspaceActionDialog.error),
    },
  }),
};
